<script>
import storageService from "@/services/storage.service";
import axios from "axios";
import AppIcon from "@/components/shared/AppIcon.vue";
import { parseNotificationResponse } from "@/helpers/notificationFormatter";
import {
  progress,
  refreshApplicationProgress,
} from "@/services/applicantProgress";

const NAV_ITEMS = [
  { label: "Dashboard", to: "/", icon: "home", exact: true },
  {
    label: "Application Status",
    to: "/view_log",
    icon: "file",
    match: ["/view_log", "/my_application"],
  },
  { label: "Notification", to: "/notifications", icon: "bell" },
];

export default {
  name: "ApplicantHeader",
  components: { AppIcon },
  data() {
    return {
      navItems: NAV_ITEMS,
      menuOpen: false,
      panelOpen: false,
      notifCount: 0,
      notifs: [],
    };
  },
  computed: {
    // Dashboard, status log and notifications unlock once the form is submitted.
    unlocked() {
      return progress.submitted;
    },
    homeLink() {
      return this.unlocked ? "/" : "/membership_application";
    },
    latest() {
      return this.notifs.slice(0, 5);
    },
    badge() {
      return this.notifCount > 99 ? "99+" : String(this.notifCount);
    },
  },
  watch: {
    "$route.path"() {
      this.menuOpen = false;
      this.panelOpen = false;
    },
    unlocked(value) {
      if (value) this.fetchNotifications();
    },
  },
  mounted() {
    if (!progress.known) refreshApplicationProgress();
    if (this.unlocked) this.fetchNotifications();
    document.addEventListener("click", this.onDocumentClick);
    document.addEventListener("keydown", this.onKeydown);
    if (this.$emitter) this.$emitter.on("notifications:read", this.onNotificationsRead);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.onDocumentClick);
    document.removeEventListener("keydown", this.onKeydown);
    if (this.$emitter) this.$emitter.off("notifications:read", this.onNotificationsRead);
  },
  methods: {
    isActive(item) {
      const path = this.$route.path;
      if (item.exact) return path === item.to;
      return (item.match || [item.to]).some((p) => path.startsWith(p));
    },
    fetchNotifications() {
      axios
        .post("/applications/kick_notif/", {
          token: storageService.getItem("accessToken"),
        })
        .then((response) => {
          const { items, count } = parseNotificationResponse(response.data);
          this.notifs = items;
          this.notifCount = count;
        })
        .catch((error) => console.error("Could not load notifications:", error));
    },
    markAsRead() {
      if (!this.notifCount) return;
      axios
        .post("/applications/kick_notif/", {
          token: storageService.getItem("accessToken"),
          read: true,
        })
        .then((response) => {
          // keep the list as is so unread items stay highlighted while the panel is open
          this.notifCount = parseNotificationResponse(response.data).count;
        })
        .catch((error) => console.error("Could not mark notifications as read:", error));
    },
    togglePanel() {
      this.panelOpen = !this.panelOpen;
      if (this.panelOpen) {
        this.menuOpen = false;
        this.markAsRead();
      }
    },
    // clicking a notification closes the panel and opens the notifications page
    openNotification() {
      this.panelOpen = false;
      this.$router.push("/notifications");
    },
    onNotificationsRead() {
      this.notifCount = 0;
    },
    onDocumentClick(event) {
      const wrap = this.$refs.bellWrap;
      if (this.panelOpen && wrap && !wrap.contains(event.target)) {
        this.panelOpen = false;
      }
      const nav = this.$refs.navWrap;
      if (this.menuOpen && nav && !nav.contains(event.target)) {
        this.menuOpen = false;
      }
    },
    onKeydown(event) {
      if (event.key === "Escape") {
        this.panelOpen = false;
        this.menuOpen = false;
      }
    },
  },
};
</script>

<template>
  <header class="ah">
    <div class="ah__inner">
      <router-link :to="homeLink" class="ah__brand" aria-label="Eastern Credit Union home">
        <img src="@/assets/images/ECU/ECU-logo.jpg" alt="Eastern Credit Union" />
      </router-link>
      <span v-if="unlocked" class="ah__divider ah__divider--brand" aria-hidden="true"></span>

      <div v-if="unlocked" ref="navWrap" class="ah__navwrap">
        <button
          type="button"
          class="ah__burger"
          :aria-expanded="menuOpen ? 'true' : 'false'"
          aria-controls="ah-nav"
          aria-label="Toggle navigation"
          @click="menuOpen = !menuOpen"
        >
          <AppIcon :name="menuOpen ? 'x' : 'menu'" :size="18" />
        </button>

        <nav id="ah-nav" class="ah__nav" :class="{ 'is-open': menuOpen }" aria-label="Main">
          <router-link
            v-for="item in navItems"
            :key="item.to"
            :to="item.to"
            class="ah__link"
            :class="{ 'is-active': isActive(item) }"
            :aria-current="isActive(item) ? 'page' : null"
          >
            <AppIcon :name="item.icon" :size="16" />
            <span>{{ item.label }}</span>
          </router-link>
        </nav>
      </div>

      <div class="ah__actions">
        <div v-if="unlocked" ref="bellWrap" class="ah__bellwrap">
          <button
            type="button"
            class="ah__bell"
            :aria-expanded="panelOpen ? 'true' : 'false'"
            aria-haspopup="true"
            :aria-label="notifCount ? `Notifications, ${notifCount} unread` : 'Notifications'"
            @click="togglePanel"
          >
            <AppIcon name="bell" :size="21" :stroke="1.7" />
            <span v-if="notifCount > 0" class="ah__badge">{{ badge }}</span>
          </button>

          <transition name="ah-pop">
            <div v-if="panelOpen" class="ah__panel" role="dialog" aria-label="Recent notifications">
              <div class="ah__panel-head">
                <h6>Notifications</h6>
                <router-link to="/notifications">View all</router-link>
              </div>
              <ul v-if="latest.length" class="ah__panel-list">
                <li
                  v-for="(n, idx) in latest"
                  :key="idx"
                  :class="{ 'is-unread': n.unread }"
                  role="link"
                  tabindex="0"
                  @click="openNotification"
                  @keydown.enter.prevent="openNotification"
                >
                  <strong :title="n.title">{{ n.title }}</strong>
                  <p v-if="n.message" :title="n.message">{{ n.message }}</p>
                  <small v-if="n.date"><AppIcon name="clock" :size="10" /> {{ n.date }}</small>
                </li>
              </ul>
              <div v-else class="ah__panel-empty">
                <AppIcon name="bell" :size="21" />
                <span>You're all caught up</span>
              </div>
            </div>
          </transition>
        </div>

        <span v-if="unlocked" class="ah__divider" aria-hidden="true"></span>

        <router-link to="/logout?logout" class="ah__logout">
          <AppIcon name="logout" :size="16" />
          <span>Logout</span>
        </router-link>
      </div>
    </div>
  </header>
</template>

<style scoped>
.ah {
  position: sticky;
  top: 0;
  z-index: 100;
  background: rgba(255, 255, 255, 0.86);
  -webkit-backdrop-filter: saturate(160%) blur(11.2px);
  backdrop-filter: saturate(160%) blur(11.2px);
  box-shadow: 0 4.8px 22.4px rgba(20, 40, 90, 0.06);
}
.ah__inner {
  max-width: 1320px;
  margin: 0 auto;
  padding: 0 19.2px;
  height: 80px;
  display: flex;
  align-items: center;
  gap: 14.4px;
}
.ah__brand {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
.ah__brand img {
  height: 65.2px;
  width: auto;
  display: block;
  aspect-ratio: 250 / 109;
  object-fit: contain;
}
.ah__divider {
  width: 1px;
  height: 40px;
  background: linear-gradient(180deg, transparent, #d9dce8, transparent);
  flex-shrink: 0;
}

/* ---------- nav ---------- */
.ah__navwrap {
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
}
.ah__nav {
  display: flex;
  align-items: center;
  gap: 4.8px;
}
.ah__link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8.8px 16px;
  border-radius: 9.6px;
  font-size: var(--ecu-fs-body);
  font-weight: 600;
  color: var(--ecu-navy, #0b1f4b);
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease,
    transform 0.2s ease;
}
.ah__link:hover {
  background: #fff1e6;
  color: var(--ecu-orange-dark, #e84d00);
}
.ah__link.is-active {
  color: #fff;
  background: linear-gradient(135deg, #ff8a2a 0%, #f2610f 100%);
  box-shadow: 0 6.4px 16px rgba(242, 97, 15, 0.28);
}
.ah__burger {
  display: none;
  width: 35.2px;
  height: 35.2px;
  border: 0;
  border-radius: 9.6px;
  background: #fff1e6;
  color: var(--ecu-orange-dark, #e84d00);
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

/* ---------- right side ---------- */
.ah__actions {
  display: flex;
  align-items: center;
  gap: 14.4px;
  margin-left: auto;
}
.ah__bellwrap {
  position: relative;
}
.ah__bell {
  position: relative;
  width: 36.8px;
  height: 36.8px;
  border: 0;
  border-radius: 11.2px;
  background: transparent;
  color: var(--ecu-navy, #0b1f4b);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;
}
.ah__bell:hover,
.ah__bell[aria-expanded="true"] {
  background: #fff1e6;
}
.ah__badge {
  position: absolute;
  top: 2px;
  right: 1px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 799.2px;
  background: #ef1c1c;
  color: #fff;
  font-size: 8.8px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  box-shadow: 0 0 0 2px #fff;
}
.ah__logout[data-v-70dbc01a] {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8.8px 17.6px;
    border-radius: 9.6px;
    background: linear-gradient(135deg, #ff7a1a00 0%, #f2610f00 100%);
    color: #ff0000;
    border: 1px solid red;
    font-size: var(--ecu-fs-body);
    font-weight: 600;
    text-decoration: none;
    /* box-shadow: 0 6.4px 16px rgba(242, 97, 15, 0.25); */
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.ah__logout[data-v-70dbc01a]:hover {
    color: #fff;
    transform: translateY(-1px);
    box-shadow: 0 9.6px 20.8px rgb(242 15 15 / 34%);
    background: red;
}

/* ---------- notification panel ---------- */
.ah__panel {
  position: absolute;
  right: 0;
  top: calc(100% + 9.6px);
  width: 288px;
  max-width: calc(100vw - 19.2px);
  background: #fff;
  border-radius: 14.4px;
  border: 1px solid #eef0f6;
  box-shadow: 0 19.2px 48px rgba(20, 40, 90, 0.18);
  overflow: hidden;
  transform-origin: top right;
}

.ah__panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12.8px 14.4px;
  border-bottom: 1px solid #f0f1f6;
}
.ah__panel-head h6 {
  margin: 0;
  font-size: var(--ecu-fs-body);
  font-weight: 700;
  color: var(--ecu-navy, #0b1f4b);
}
.ah__panel-head a {
  font-size: var(--ecu-fs-small);
  font-weight: 600;
  color: var(--ecu-orange-dark, #e84d00);
  text-decoration: none;
}
.ah__panel-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 256px;
  overflow-y: auto;
}
.ah__panel-list li {
  padding: 11.2px 14.4px;
  border-bottom: 1px solid #f4f5fa;
  position: relative;
}
.ah__panel-list li {
  cursor: pointer;
}
.ah__panel-list li:focus-visible {
  outline: 2px solid var(--ecu-orange, #f97316);
  outline-offset: -2px;
}
.ah__panel-list li:last-child {
  border-bottom: 0;
}
.ah__panel-list li.is-unread {
  background: #fffaf5;
}
.ah__panel-list li.is-unread::before {
  content: "";
  position: absolute;
  left: 5.6px;
  top: 17.6px;
  width: 4.8px;
  height: 4.8px;
  border-radius: 50%;
  background: var(--ecu-orange, #f97316);
}
.ah__panel-list strong,
.ah__panel-list p {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  overflow-wrap: anywhere;
}
.ah__panel-list strong {
  display: -webkit-box;
  font-size: var(--ecu-fs-body);
  color: var(--ecu-navy, #0b1f4b);
}
.ah__panel-list li:not(.is-unread) strong {
  font-weight: 500;
}
.ah__panel-list p {
  margin: 2.4px 0 3.2px;
  font-size: var(--ecu-fs-small);
  color: #5f6579;
}
.ah__panel-list small {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: var(--ecu-fs-small);
  color: #8b90a3;
}
.ah__panel-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6.4px;
  padding: 25.6px 14.4px;
  color: #8b90a3;
  font-size: var(--ecu-fs-body);
}
.ah-pop-enter-active,
.ah-pop-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}
.ah-pop-enter-from,
.ah-pop-leave-to {
  opacity: 0;
  transform: translateY(-6px) scale(0.98);
}

/* ---------- responsive ---------- */
@media (max-width: 991px) {
  .ah__inner {
    height: 54.4px;
    padding: 0 12.8px;
    gap: 9.6px;
  }
  .ah__brand img {
    height: 36.8px;
  }
  .ah__divider--brand {
    display: none;
  }
  .ah__navwrap {
    flex: 0 0 auto;
  }
  .ah__burger {
    display: inline-flex;
  }
  .ah__nav {
    display: none;
    position: absolute;
    left: 9.6px;
    right: 9.6px;
    top: calc(100% + 6.4px);
    flex-direction: column;
    align-items: stretch;
    gap: 3.2px;
    padding: 8px;
    background: #fff;
    border-radius: 14.4px;
    box-shadow: 0 19.2px 48px rgba(20, 40, 90, 0.18);
  }
  .ah__nav.is-open {
    display: flex;
  }
  .ah__actions {
    gap: 6.4px;
  }
  .ah__actions .ah__divider {
    display: none;
  }
}
@media (max-width: 575px) {
  .ah__logout {
    padding: 8.8px 10.4px;
  }
  .ah__logout span {
    display: none;
  }
}
@media (prefers-reduced-motion: reduce) {
  .ah__link,
  .ah__logout,
  .ah-pop-enter-active,
  .ah-pop-leave-active {
    transition: none;
  }
}
</style>